import { ReactNode, useCallback, useState } from "react";
import { useConfirm, useWindowSize, useWorkspace } from "@/hooks";
import { useNavigate } from "react-router";
import { useToast, Table, Button, Card, CardContent, CardHeader, Division, EmptyState, Flex, Heading, Icon, Icons, Paragraph } from "@ir/ui";
import useDeductionIncomplete from "./hook/useDeductionIncomplete";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { DocumentSignedURLRepository } from "@/infrastructure/repositories/document";
import { GetDocumentSignedURLUseCase } from "@/core/usecases/document";
import { DeductionDocument } from "@/core/entities/deduction";
import BackupDocumentsTableColumns from "./BackupDocumentsTable";
import { WorkspacePermissions } from "@/core/permissions";
import { HasAnyPermission } from "@/presentation/workspace";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import { getMaxFilesAllowed } from "./context/DeductionIncompleteContext";
import { WORKSPACE_ROUTES } from "@/routes/workspace/utils/Workspace.router.utils";
import { createLogger } from "@/infrastructure/services/logger";

// GraphQL client
const client = GraphQlAdapter.getInstance().getClient();
// Deduction document signed URL repository
const documentSignedURLRepository = new DocumentSignedURLRepository(client);

interface DataType {
  operation?: ReactNode;
}

const BackupDocuments = () => {
  const logger = useCallback(() => createLogger("BackupDocuments"), []);
  const toast = useToast();
  const navigate = useNavigate();
  const { settings: workspaceSettings } = useWorkspace();
  const {
    documents,
    setDocuments,
    isLoadingDeductionStatus,
    deductionResolutionStatus,
    purchaserKey,
    importedFiles,
    importedFilesAsDocuments,
    deleteDeductionBackupDocument,
  } = useDeductionIncomplete();
  const confirm = useConfirm();
  const { drawerInfo, setIsDocumentViewerOpen, setViewerDocuments, isDocumentViewerOpen, setCloseDocumentViewer } = useDeductionDrawer();
  const { width } = useWindowSize();

  const [downloadingDocuments, setDownloadingDocuments] = useState<Array<number>>([]);
  const [deletingDocuments, setDeletingDocuments] = useState<Array<number>>([]);

  const fetchDocumentSignedURL = useCallback(async (file_url: string, file_type: string, downloadable_file_name: string) => {
    const response = await new GetDocumentSignedURLUseCase(documentSignedURLRepository).execute(file_url, file_type, downloadable_file_name);
    return response;
  }, []);

  const handleDocumentDownload = useCallback(
    async (deductionBackupDocument: DeductionDocument) => {
      setDownloadingDocuments(prev => [...prev, deductionBackupDocument.id]);
      if (deductionBackupDocument.signedUrl && !deductionBackupDocument.isExpired) {
        setDownloadingDocuments(prev => prev.filter(id => id !== deductionBackupDocument.id));
        globalThis.open(deductionBackupDocument.signedUrl, "_blank");
        return;
      }
      if (deductionBackupDocument.filePath === "") {
        toast.warning({ content: "Invalid document" });
        setDownloadingDocuments(prev => prev.filter(id => id !== deductionBackupDocument.id));
        return;
      }

      let downloadableFileName = deductionBackupDocument.fileDisplayName;
      const fileExtension = deductionBackupDocument.fileDisplayName.split(".").pop();
      if (deductionBackupDocument.documentType?.toUpperCase() === "BACKUP" && drawerInfo?.invoiceNumber) {
        downloadableFileName = `${drawerInfo?.invoiceNumber}.${fileExtension}`;
      }

      const { signedUrl } = await fetchDocumentSignedURL(deductionBackupDocument.filePath, deductionBackupDocument.fileMimeType, downloadableFileName);

      setDownloadingDocuments(prev => prev.filter(id => id !== deductionBackupDocument.id));
      const deductionDocumentIndex = documents.findIndex(d => d.id === deductionBackupDocument.id);
      if (signedUrl === null) {
        toast.error({ content: "Failed to download document" });
      } else {
        deductionBackupDocument.signedUrl = signedUrl;
        const newDocuments = [...documents];
        newDocuments[deductionDocumentIndex] = deductionBackupDocument;
        setDocuments(newDocuments);

        globalThis.open(deductionBackupDocument.signedUrl, "_blank");
      }
    },
    [fetchDocumentSignedURL, documents, setDocuments, toast, drawerInfo?.invoiceNumber]
  );

  const handleDocumentDelete = useCallback(
    async (backupDocumentId: number) => {
      const confirmResult = await confirm({
        title: "Delete Document",
        message: "Are you sure you want to delete this document?",
        confirmText: "Delete",
        cancelText: "Cancel",
      });

      if (!confirmResult) {
        return;
      }

      try {
        setDeletingDocuments(prev => [...prev, backupDocumentId]);
        await deleteDeductionBackupDocument([backupDocumentId]);
        toast.success({ content: "Document deleted" });
      } catch (error) {
        logger().error("Error deleting document", error);
        toast.error({ content: "Failed to delete document" });
      } finally {
        setDeletingDocuments(prev => prev.filter(id => id !== backupDocumentId));
      }
    },
    [confirm, deleteDeductionBackupDocument, toast, logger]
  );

  const columns = BackupDocumentsTableColumns({
    isLoadingDeductionStatus,
    workspaceSettings,
    downloadingDocuments,
    deletingDocuments,
    handleDocumentDownload,
    handleDocumentDelete,
  });

  const getSkeletonData = (count: number = 2): DeductionDocument[] => {
    const currentDate = new Date().toLocaleDateString();
    return Array.from({ length: count }, (_, index) => {
      return new DeductionDocument({
        id: index,
        fileName: `Document ${index + 1}`,
        fileSize: 1024,
        createdAt: currentDate,
        updatedAt: currentDate,
        documentType: `Document Type ${index + 1}`,
        documentTypeKey: `documentTypeKey ${index + 1}`,
        fileMimeType: "application/pdf",
        fileDisplayName: `Document ${index + 1}`,
        filePath: "",
        signedUrl: "",
        deletable: true,
        isProcessed: "PENDING",
      });
    });
  };

  // Check if all files have errors
  const hasAllFilesErrored = (): boolean => {
    if (importedFilesAsDocuments.length === 0) return false;
    return importedFilesAsDocuments.every(doc => doc.isProcessed === "FAILED");
  };

  // Check if any files are currently being uploaded
  const hasUploadingFiles = (): boolean => {
    return importedFiles.some(file => file.isPending);
  };

  // Get only successful files for document viewer
  const getSuccessfulFiles = (): DeductionDocument[] => {
    return importedFilesAsDocuments.filter(doc => doc.isProcessed !== "FAILED");
  };

  const handleOpenDocumentViewer = () => {
    setIsDocumentViewerOpen(true);
    // Only show successful files in the document viewer
    setViewerDocuments(getSuccessfulFiles());
  };

  const handleCloseDocumentViewer = () => {
    setCloseDocumentViewer();
  };

  // Check if upload button should be disabled
  const isUploadButtonDisabled = (): boolean => {
    const maxFilesAllowed = getMaxFilesAllowed(purchaserKey);
    const hasProcessingFiles = importedFiles.some(file => file.isPending);
    const isAtFileLimit = importedFilesAsDocuments.length >= maxFilesAllowed;

    return isAtFileLimit || hasProcessingFiles;
  };

  // Get tooltip message for disabled upload button
  const getUploadButtonTooltip = (): string | undefined => {
    if (!isUploadButtonDisabled()) return undefined;

    const maxFilesAllowed = getMaxFilesAllowed(purchaserKey);
    return importedFilesAsDocuments.length >= maxFilesAllowed ? `Maximum ${maxFilesAllowed} files allowed` : "Files are currently being processed";
  };

  return (
    <Card className={`bg-white ${isLoadingDeductionStatus ? "mb-4" : "mb-3"}`}>
      <CardHeader className="py-2">
        <Flex justifyContent="space-between" align="center">
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Documents
          </Heading>

          <Flex>
            <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTION_DOCUMENT_VIEWER]}>
              {width > 1200 && importedFilesAsDocuments.length > 0 && !isLoadingDeductionStatus && !hasAllFilesErrored() && !hasUploadingFiles() && (
                <Button
                  size="medium"
                  variant="outline"
                  color="grey"
                  onPress={isDocumentViewerOpen ? handleCloseDocumentViewer : handleOpenDocumentViewer}
                  className="mx-1"
                >
                  <Icon slot="start" name={Icons.File} />
                  {isDocumentViewerOpen ? "Hide Documents" : "View Documents"}
                </Button>
              )}
            </HasAnyPermission>
            {(deductionResolutionStatus === "processing" || importedFiles.length > 0) && (
              <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_UPLOAD]}>
                <Button
                  size="medium"
                  variant="outline"
                  color="primary"
                  onPress={() => navigate(`/workspace${WORKSPACE_ROUTES.INTELLI_UPLOAD}`)}
                  isDisabled={isUploadButtonDisabled()}
                  htmlTitle={getUploadButtonTooltip()}
                  className={isLoadingDeductionStatus ? "button-disabled-tooltip ms-1" : "ms-1"}
                >
                  <Icon slot="start" name={Icons.UploadCloud} />
                  Upload Backup
                </Button>
              </HasAnyPermission>
            )}
          </Flex>
        </Flex>
      </CardHeader>
      <CardContent>
        <Flex>
          <Division className="flex-1">
            {importedFilesAsDocuments.length > 0 || !isLoadingDeductionStatus ? (
              <Table<DeductionDocument & DataType>
                columns={columns}
                dataSource={isLoadingDeductionStatus ? getSkeletonData() : importedFilesAsDocuments}
                border="row"
                aria-label="Table"
                aria-labelledby="table"
                sticky={true}
                scroll={{ x: "100%" }}
                className="mb-0"
              />
            ) : (
              <EmptyState
                className="bg-white border-1 border-color-grey-25 py-3 flex-1"
                icon={Icons.File}
                title={
                  <Heading level={6} className="text-grey-700 font-bold mt-3">
                    No documents found
                  </Heading>
                }
                description={
                  <Paragraph className="text-grey-500 font-regular mb-0">This is where you can view backup documents uploaded by the client</Paragraph>
                }
              />
            )}
          </Division>
        </Flex>
      </CardContent>
    </Card>
  );
};

export default BackupDocuments;
